import React from "react";
import { EntityAuditFooter } from "@/components/shared/EntityAuditFooter";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Package, Layers, Tag, Ruler } from "lucide-react";
import EntityContextPanel from "@/components/shared/EntityContextPanel";

interface ProductTypeDetailDrawerProps {
  item: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (item: any) => void;
}

export function ProductTypeDetailDrawer({
  item,
  open,
  onOpenChange,
  onEdit,
}: ProductTypeDetailDrawerProps) {
  if (!item) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-6 flex flex-col justify-between z-[10000] overflow-y-auto">
        <div className="space-y-6">
          <SheetHeader className="p-0 space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-50 text-indigo-700 rounded-lg">
                <Package className="w-5 h-5" />
              </span>
              <div>
                <SheetTitle className="text-lg font-bold text-slate-900 font-mono">
                  {item.code}
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500">
                  Cấu hình loại sản phẩm bao bì
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Details Card */}
          <div className="space-y-4 text-xs">
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Tag className="w-3.5 h-3.5 text-slate-400" /> Mã / Tên loại
                </span>
                <span className="font-mono font-bold text-slate-900 text-sm">
                  {item.code}
                </span>
              </div>

              <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Layers className="w-3.5 h-3.5 text-slate-400" /> Danh mục chính
                </span>
                <Badge variant="outline" className="bg-white border-slate-200 text-slate-800 font-semibold">
                  {item.category || "Chưa phân loại"}
                </Badge>
              </div>

              <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Package className="w-3.5 h-3.5 text-slate-400" /> Nhóm cấu trúc
                </span>
                <span className="font-semibold text-slate-800">
                  {item.group || "—"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Ruler className="w-3.5 h-3.5 text-slate-400" /> Đơn vị tính
                </span>
                <Badge variant="outline" className="bg-indigo-50 border-indigo-200 text-indigo-700 font-mono font-bold">
                  {item.unit || "—"}
                </Badge>
              </div>
            </div>

            {/* Quick Helper Note */}
            <div className="p-3 bg-amber-50/60 border border-amber-200/80 rounded-lg text-amber-900 text-[11px] leading-relaxed">
              <strong>Ghi chú:</strong> Loại sản phẩm được dùng để chuẩn hóa form tạo sản phẩm mới, thiết lập quy cách in ấn và phân loại quản trị đơn hàng.
            </div>

            {/* Context Panel (Comments, Attachments, Audit Logs) */}
            <div className="pt-2 border-t border-slate-200">
              <EntityContextPanel entityType="product_type" entityId={item.id || item.code} />
              <EntityAuditFooter entityType="product_type" entityId={item.id || item.code} fallback={{ created: item.createdAt, updated: item.updatedAt }} className="mt-3 text-[11px] text-slate-500 flex items-center gap-x-3 gap-y-1 flex-wrap" />
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
          <Button
            variant="outline"
            size="sm"
            className="h-9 px-4 text-xs font-medium text-slate-700"
            onClick={() => onOpenChange(false)}
          >
            Đóng
          </Button>
          <Button
            size="sm"
            className="h-9 px-4 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 shadow-sm"
            onClick={() => {
              onOpenChange(false);
              onEdit?.(item);
            }}
          >
            <Edit className="w-3.5 h-3.5" />
            Chỉnh Sửa
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
